// App shell: top bar (with the candy-stripe accent bar) plus the scrollable
// main content area. Auth gates the whole shell. There is no department
// sidebar (this app has one flat tile menu, not Cockpit's department nav),
// so the shell is simpler than Cockpit's: just Header + <Outlet>.
//
// Help comes in two experiences, chosen per store by the api (see
// lib/helpExperience.ts). `new` is the Service Desk Help Center. `legacy` is the
// original help slide-out and bug icon, unchanged from before the Service Desk.

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useServiceDeskUnread, type HelpCenterView } from '@ampersandbrands/ui';
import { Header } from './Header';
import { AuthGate } from './AuthGate';
import { BottomNav } from './BottomNav';
import { ChatBubble } from './ChatBubble';
import { RouteErrorBoundary } from './RouteErrorBoundary';
import { resolveRouteTitle } from './routeTitles';
import { HelpAndSupport } from '@/components/HelpAndSupport';
import { LegacyHelpAndSupport } from '@/components/LegacyHelpAndSupport';
import { parseHelpArticleId } from '@/lib/articleLink';
import { captureSweetSpotScreenshot, type SupportScreenshot } from '@/lib/supportScreenshot';
import { createServiceDeskClient, findArticleIdByConfluenceId } from '@/lib/serviceDeskClient';
import { useHelpExperience } from '@/lib/helpExperience';
import { useDocumentTitle } from '@/lib/useDocumentTitle';
import { useSession } from '@/lib/useSession';
import { useEffectiveStoreNo } from '@/lib/useStores';

/** The chrome both experiences share: header, page, help panel, chat, bottom nav. */
function Frame({ header, help }: { header: ReactNode; help: ReactNode }) {
  // Keyed by pathname so navigating away from a crashed page resets the boundary.
  const { pathname } = useLocation();
  return (
    <div className="app-shell flex h-screen flex-col overflow-hidden bg-candy-cream text-candy-ink dark:bg-candy-dark-bg dark:text-candy-dark-ink">
      {header}
      <main data-sweetspot-main className="main-with-bottom-nav min-w-0 flex-1 overflow-y-auto px-4 py-6 sm:px-8">
        <RouteErrorBoundary key={pathname}>
          <Outlet />
        </RouteErrorBoundary>
      </main>
      {help}
      <ChatBubble />
      <BottomNav />
    </div>
  );
}

/** The Service Desk Help Center (stores on the pilot list). */
function NewExperienceLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [helpOpen, setHelpOpen] = useState(false);
  // Set when a link such as /?help_ticket=42 (the request-received email) asks to open one request.
  const [helpView, setHelpView] = useState<HelpCenterView | undefined>(undefined);

  // /?help_ticket=42 opens the Help Center on request 42, then drops the parameter so a refresh does not reopen it.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const id = Number(params.get('help_ticket'));
    if (!Number.isInteger(id) || id <= 0) return;
    setHelpView({ ticket: id });
    setHelpOpen(true);
    params.delete('help_ticket');
    const search = params.toString();
    navigate({ pathname: location.pathname, search: search ? `?${search}` : '' }, { replace: true });
  }, [location.pathname, location.search, navigate]);

  // /?help_article=61636610 (a Confluence page id) opens the Help Center on the article imported from that
  // page at full width, then drops the parameter. No matching article opens the Help Center home.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const raw = params.get('help_article');
    if (raw === null) return;
    params.delete('help_article');
    const search = params.toString();
    navigate({ pathname: location.pathname, search: search ? `?${search}` : '', hash: location.hash }, { replace: true });
    setHelpView(undefined);
    setHelpOpen(true);
    void findArticleIdByConfluenceId(raw.trim()).then((id) => {
      if (id !== null) setHelpView({ article: id, wide: true });
    });
  }, [location.pathname, location.search, location.hash, navigate]);

  // /?help_article_id=12 opens the Help Center on that article by its own id, for articles written directly in
  // the knowledge base (no Confluence page id). Same full-width behaviour, then the parameter is dropped.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const raw = params.get('help_article_id');
    if (raw === null) return;
    params.delete('help_article_id');
    const search = params.toString();
    navigate({ pathname: location.pathname, search: search ? `?${search}` : '', hash: location.hash }, { replace: true });
    const id = parseHelpArticleId(raw);
    setHelpView(id === null ? undefined : { article: id, wide: true });
    setHelpOpen(true);
  }, [location.pathname, location.search, location.hash, navigate]);

  const { user } = useSession();
  const storeNo = useEffectiveStoreNo();

  // One Service Desk client for the session. It reads the store a corporate
  // user picked in the header at call time, so switching stores needs no new
  // client.
  const storeNoRef = useRef(storeNo);
  storeNoRef.current = storeNo;
  const client = useMemo(() => createServiceDeskClient(() => storeNoRef.current), []);
  const { count: unread, refresh } = useServiceDeskUnread(client, { intervalMs: 60_000, enabled: !!user });

  return (
    <Frame
      header={
        <Header
          onHelpOpen={() => {
            setHelpView(undefined);
            setHelpOpen(true);
          }}
          helpUnread={unread}
        />
      }
      help={
        <HelpAndSupport
          open={helpOpen}
          onClose={() => {
            setHelpOpen(false);
            setHelpView(undefined);
          }}
          initialView={helpView}
          client={client}
          onActivity={() => void refresh()}
        />
      }
    />
  );
}

/** The original help slide-out and report-an-issue bug icon (every store not on the pilot list). */
function LegacyExperienceLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [helpOpen, setHelpOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [screenshot, setScreenshot] = useState<SupportScreenshot | null>(null);
  // Confluence page id from a ?help_article= link. The old slide-out can show any page by id.
  const [articleOverride, setArticleOverride] = useState<string | null>(null);

  // /?help_article=61636610 opens the old slide-out on that Confluence page, then drops the parameter.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const raw = params.get('help_article');
    if (raw === null) return;
    params.delete('help_article');
    const search = params.toString();
    navigate({ pathname: location.pathname, search: search ? `?${search}` : '', hash: location.hash }, { replace: true });
    const id = raw.trim();
    if (!/^\d{1,32}$/.test(id)) return;
    setArticleOverride(id);
    setHelpOpen(true);
  }, [location.pathname, location.search, location.hash, navigate]);

  // Capture before the dialog opens, or the dialog itself lands in the picture.
  // When the reader came from the help panel, let it slide out first.
  async function openReport({ closeHelpFirst = false } = {}) {
    if (closeHelpFirst) {
      setHelpOpen(false);
      await new Promise((resolve) => window.setTimeout(resolve, 250));
    }
    setScreenshot(await captureSweetSpotScreenshot());
    setReportOpen(true);
  }

  return (
    <Frame
      header={
        <Header
          onHelpOpen={() => {
            setArticleOverride(null);
            setHelpOpen(true);
          }}
          onReportIssueOpen={() => void openReport()}
        />
      }
      help={
        <LegacyHelpAndSupport
          helpOpen={helpOpen}
          onHelpClose={() => {
            setHelpOpen(false);
            setArticleOverride(null);
          }}
          reportOpen={reportOpen}
          onReportOpen={() => void openReport({ closeHelpFirst: true })}
          onReportClose={() => setReportOpen(false)}
          screenshot={screenshot}
          articleOverride={articleOverride}
        />
      }
    />
  );
}

/** Waits for the api to say which experience this store gets, then renders it. */
function ExperienceSwitch() {
  const experience = useHelpExperience();
  if (experience === 'new') return <NewExperienceLayout />;
  if (experience === 'legacy') return <LegacyExperienceLayout />;
  // Brief: the answer is one small request. No help buttons until it lands, so
  // a store never sees the wrong ones flash.
  return <Frame header={<Header />} help={null} />;
}

export function Layout() {
  const { pathname } = useLocation();
  useDocumentTitle(resolveRouteTitle(pathname));
  return (
    <AuthGate>
      <ExperienceSwitch />
    </AuthGate>
  );
}
